import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { ceebAcceptApi, CeebAcceptCheck } from '../services/api';
import { lokaleLabel } from '../utils/plural';

/**
 * Potwierdzenie AKCEPTACJI protokołu w CEEB (zielony przycisk superadmina) — PipelineView i TermsTab.
 *
 * Akceptacja jest NIEODWRACALNA i zatwierdza w rejestrze to, co JEST w CEEB. Dlatego modal najpierw
 * pyta serwer o bramkę (`ceeb-accept-check`) i pokazuje fakty, zanim pozwoli kliknąć:
 *  - blokada twarda (nie „Wprowadzony", już zaakceptowany, brak numeru sprawy) → tylko „Zamknij",
 *  - lokale z danymi z terenu POZA CEEB (incydent 2026-10-08, Adama Kawika 12/14/16: kopie lokali
 *    z poprzedniej kontroli) → lista lokali i obowiązkowe pole „poprawiono ręcznie w CEEB",
 *  - lokale bez kontroli w tym cyklu → informacja, że w CEEB zostają dane poprzedniej kontroli.
 */
export interface CeebAcceptTarget {
  buildingId: string;
  address: string;
}

interface Props {
  target: CeebAcceptTarget | null;
  onCancel: () => void;
  onConfirm: (buildingId: string, opts: { acknowledgePending: boolean }) => void;
}

const box = (tone: 'deny' | 'info'): React.CSSProperties => ({
  margin: 'var(--space-3) 0 0',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: `var(--border-width) solid ${tone === 'deny' ? 'var(--status-deny-bar)' : 'var(--status-rec-bar)'}`,
  background: tone === 'deny' ? 'var(--status-deny-bg)' : 'var(--status-rec-bg)',
  color: 'var(--text-primary)',
  fontSize: 'var(--text-caption)',
});

export default function CeebAcceptModal({ target, onCancel, onConfirm }: Props) {
  const [check, setCheck] = useState<CeebAcceptCheck | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const buildingId = target?.buildingId ?? null;

  useEffect(() => {
    setCheck(null);
    setLoadError(null);
    setAcknowledged(false);
    if (!buildingId) return undefined;
    let aktualne = true;
    ceebAcceptApi.check(buildingId)
      .then((c) => { if (aktualne) setCheck(c); })
      .catch((e) => { if (aktualne) setLoadError(e instanceof Error ? e.message : 'Nie udało się sprawdzić protokołu'); });
    return () => { aktualne = false; };
  }, [buildingId]);

  const hardBlock = !!(check?.blocked && !check.requiresAcknowledge);
  const canConfirm = !!check && !hardBlock && !check.busy && (!check.requiresAcknowledge || acknowledged);
  const inCeeb = check ? Math.max(check.aptTerminal - check.aptPendingCeeb, 0) : 0;

  return (
    <ModalShell
      open={!!target}
      title={target ? `Zaakceptować protokół w CEEB — ${target.address}?` : undefined}
      onClose={onCancel}
      width="min(92vw, 560px)"
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel}>{hardBlock ? 'Zamknij' : 'Anuluj'}</Button>
          {!hardBlock && (
            <Button
              variant="primary"
              icon="check"
              data-testid="ceeb-accept-confirm"
              disabled={!canConfirm}
              style={canConfirm ? { background: 'var(--status-ok, #2e7d32)', borderColor: 'var(--status-ok, #2e7d32)' } : undefined}
              onClick={() => { if (target && canConfirm) onConfirm(target.buildingId, { acknowledgePending: !!check?.requiresAcknowledge }); }}
            >
              {check?.requiresAcknowledge ? 'Akceptuj mimo to' : 'Akceptuj w CEEB'}
            </Button>
          )}
        </>
      )}
    >
      {!check && !loadError && <p style={{ margin: 0, color: 'var(--text-muted)' }}>Sprawdzam protokół…</p>}
      {loadError && <p role="alert" style={{ margin: 0, color: 'var(--status-deny)' }}>{loadError}</p>}
      {check && (
        <div data-testid="ceeb-accept-check">
          <p style={{ margin: 0 }}>
            Numer sprawy: <strong>{check.caseNumber || '—'}</strong> · status w CEEB: <strong>{check.ceebStatus || 'nieznany'}</strong>
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
            Lokale z kontrolą wysłaną z aplikacji: {inCeeb} z {check.aptTerminal}.
          </p>

          {hardBlock && check.blocked && (
            <div role="alert" style={box('deny')}>{check.blocked.error}</div>
          )}

          {check.requiresAcknowledge && (
            <div role="alert" data-testid="ceeb-accept-pending" style={box('deny')}>
              <p style={{ margin: '0 0 var(--space-2)', display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontWeight: 'var(--weight-semibold)' }}>
                <span style={{ display: 'inline-flex', color: 'var(--status-deny)' }}><Icon name="triangle-alert" size={16} /></span>
                Dane z terenu nie weszły do CEEB: {lokaleLabel(check.aptPendingCeeb)}
              </p>
              <p style={{ margin: '0 0 var(--space-2)' }}>
                {check.pendingApartments.length > 0 && <>Lokale: <strong>{check.pendingApartments.join(', ')}</strong>. </>}
                W CEEB jest tam coś innego (np. kopia z poprzedniej kontroli). Akceptacja zatwierdzi w rejestrze
                to, co jest w CEEB — nie to, co kominiarz wpisał w aplikacji.
              </p>
              <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  data-testid="ceeb-accept-acknowledge"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                <span>Sprawdziłem w CEEB — te lokale zostały poprawione ręcznie i mają aktualne dane.</span>
              </label>
            </div>
          )}

          {!hardBlock && check.aptOpen > 0 && (
            <div style={box('info')}>
              {lokaleLabel(check.aptOpen)} bez kontroli w tym cyklu — w CEEB zostają dla nich dane z poprzedniej kontroli.
            </div>
          )}

          {check.busy && (
            <div role="alert" style={box('deny')}>{check.busy.error}</div>
          )}

          {!hardBlock && (
            <p style={{ margin: 'var(--space-3) 0 0' }}>
              Akceptacja jest <strong>NIEODWRACALNA</strong> — status w CEEB zmieni się z „Wprowadzony" na „Zaakceptowany".
            </p>
          )}
        </div>
      )}
    </ModalShell>
  );
}
